import { useQuery } from '@tanstack/react-query';
import supabaseClient from '@/api/supabaseClient.ts';
import { useAuthContext } from '@/context/AuthContext.tsx';
import type { Tables } from '$/database.types';
import type { CompassPreviewCache } from '$/requests/GovernanceCheck.ts';

type DecisionDetail = Pick<
  Tables<'decisions'>,
  | 'id'
  | 'issue_title'
  | 'status'
  | 'governance_score'
  | 'risk_score'
  | 'primary_framework'
  | 'created_at'
  | 'completed_at'
  | 'compass_preview_cache'
> & {
  users: Pick<Tables<'users'>, 'id' | 'email' | 'role' | 'brand_role'> | null;
  organizations: Pick<Tables<'organizations'>, 'id' | 'name'> | null;
};

type AuditArtifactRow = Pick<
  Tables<'audit_artifacts'>,
  'id' | 'artifact_type' | 'ledger_hash' | 'exportable' | 'generated_at'
>;

export type SessionDetail = {
  decision: DecisionDetail;
  compass_preview_cache: CompassPreviewCache | null;
  ledger_entries: Tables<'ledger_entries'>[];
  approval_states: Tables<'approval_states'>[];
  audit_artifacts: AuditArtifactRow[];
};

async function fetchSessionDetail(decisionId: string, orgId: string): Promise<SessionDetail> {
  const { data: decision, error: decisionError } = await supabaseClient
    .from('decisions')
    .select(
      `
      id,
      issue_title,
      status,
      governance_score,
      risk_score,
      primary_framework,
      created_at,
      completed_at,
      compass_preview_cache,
      users:users(id, email, role, brand_role),
      organizations:organizations(id, name)
      `,
    )
    .eq('id', decisionId)
    .eq('org_id', orgId)
    .maybeSingle();

  if (decisionError) throw new Error(decisionError.message ?? 'Failed to fetch session detail');
  if (!decision) throw new Error('Record not found');

  const [
    { data: ledgerEntries, error: ledgerError },
    { data: approvalStates, error: approvalError },
    { data: auditArtifacts, error: artifactsError },
  ] = await Promise.all([
    supabaseClient
      .from('ledger_entries')
      .select('*')
      .eq('decision_id', decisionId)
      .order('created_at', { ascending: true }),
    supabaseClient
      .from('approval_states')
      .select('*')
      .eq('decision_id', decisionId)
      .order('created_at', { ascending: true }),
    supabaseClient
      .from('audit_artifacts')
      .select('id, artifact_type, ledger_hash, exportable, generated_at')
      .eq('decision_id', decisionId)
      .order('generated_at', { ascending: false }),
  ]);

  if (ledgerError) throw new Error(ledgerError.message ?? 'Failed to fetch ledger entries');
  if (approvalError) throw new Error(approvalError.message ?? 'Failed to fetch approval states');
  if (artifactsError) throw new Error(artifactsError.message ?? 'Failed to fetch audit artifacts');

  const compassPreviewCache = decision.compass_preview_cache as CompassPreviewCache | null;

  return {
    decision: decision as DecisionDetail,
    compass_preview_cache: compassPreviewCache,
    ledger_entries: (ledgerEntries ?? []) as Tables<'ledger_entries'>[],
    approval_states: (approvalStates ?? []) as Tables<'approval_states'>[],
    audit_artifacts: (auditArtifacts ?? []) as AuditArtifactRow[],
  };
}

const useSessionDetail = (decisionId: string | null) => {
  const { userProfile } = useAuthContext();
  const orgId = userProfile?.org_id ?? null;

  return useQuery({
    queryKey: ['session-detail', decisionId, orgId],
    queryFn: () => fetchSessionDetail(decisionId!, orgId!),
    enabled: !!decisionId && !!orgId,
  });
};

export default useSessionDetail;
